import { PLACEHOLDER_VALUE } from '@/constants';
import { getDateAsString } from '@/lib';
import { ColDef } from 'ag-grid-enterprise';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Sample } from './types';

function useSampleColDefs() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      [
        { field: 'name', headerName: t('general:name', { defaultValue: 'Name' }) },
        {
          field: 'study_id',
          headerName: t('create:run.studyId', { defaultValue: 'Study ID' }),
          hide: true,
        },
        { field: 'org_id', headerName: t('create:run.orgId', { defaultValue: 'Org ID' }), hide: true },
        {
          field: 'well_position',
          headerName: t('create:run.wellPosition', { defaultValue: 'Well Position' }),
          maxWidth: 110,
        },
        {
          field: 'demux_index',
          headerName: t('create:run.demuxIndex', { defaultValue: 'Demux Index' }),
          maxWidth: 100,
        },
        {
          field: 'updated_at',
          headerName: t('updated_at', { ns: 'general' }),
          hide: true,
          valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : ''),
        },
      ] as ColDef<Sample>[],
    [t],
  );
}

export type SampleType = {
  name: string;
  study_name: string;
  org_name: string;
  well_position: string;
  demux_index: number;
};

/**
 * Checks if all required sample fields are filled and valid.
 * - String fields must be non-empty (not just whitespace) and must not equal PLACEHOLDER_VALUE.
 * - demux_index must be an integer >= 0.
 */

/* Module-level validators to avoid recreating closures on every call */
const isValidString = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '' && v !== PLACEHOLDER_VALUE;

const isValidDemuxIndex = (v: unknown): boolean => typeof v === 'number' && Number.isInteger(v) && v >= 0;

function areAllSampleFieldsFilled(samples: SampleType[]): boolean {
  if (!Array.isArray(samples) || samples.length === 0) return false;

  return samples.every(
    sample =>
      isValidString(sample.name) &&
      isValidString(sample.study_name) &&
      isValidString(sample.org_name) &&
      isValidString(sample.well_position) &&
      isValidDemuxIndex(sample.demux_index),
  );
}

/**
 * Checks if the sample count matches the expected count.
 * @param samples Array of Sample objects
 * @param expectedCount Number of samples expected
 * @returns true if counts match, false otherwise
 */
function isSampleCountCorrect(samples: ReadonlyArray<unknown>, expectedCount: number): boolean {
  if (!Array.isArray(samples)) return false;
  return samples.length === expectedCount;
}

export { areAllSampleFieldsFilled, isSampleCountCorrect, useSampleColDefs };
